/**
 * Utility functions for extracting and handling API error messages
 */

interface ApiErrorResponse {
  data?: {
    Failed?: string;
    errors?: string[];
    error?: string;
    message?: string;
    response?: string;
  };
  message?: string;
  status?: number;
}

interface ApiResult {
  data?: {
    Failed?: string;
    errors?: string[];
    error?: string;
    message?: string;
    response?: string;
  };
  message?: string;
  status?: number;
}

/**
 * Extracts error message from API response or error object
 * Handles various error response formats:
 * - result.data.Failed (may contain JSON string with errors array)
 * - result.data.errors (array of error strings)
 * - result.data.error
 * - result.data.message
 * - result.data.response
 * - result.message
 * - error.response.data.* (same structure as result.data)
 * 
 * @param source - Either an API result object or an error object from catch block
 * @param defaultMessage - Default error message if no error can be extracted
 * @returns Extracted error message string
 */
export const extractErrorMessage = (
  source: ApiResult | ApiErrorResponse | any,
  defaultMessage: string = "An error occurred"
): string => {
  let errorMessage = defaultMessage;

  // Intermediate-ledger rejection (409): summarize instead of the misleading
  // generic fallbacks; extractValidationDetails carries the per-file reasons.
  if (
    getLedgerValidationGroups(source?.data) ||
    getLedgerValidationGroups(source?.response?.data)
  ) {
    return LEDGER_REJECTION_SUMMARY;
  }

  // Handle result object (from executePost/executeGet)
  if (source?.data) {
    // Check for Failed field (common in error responses)
    if (source.data.Failed) {
      errorMessage = source.data.Failed;
      // If Failed contains JSON string, try to parse it
      if (typeof errorMessage === 'string' && errorMessage.includes('errors')) {
        try {
          const parsed = JSON.parse(errorMessage);
          if (parsed.errors && Array.isArray(parsed.errors) && parsed.errors.length > 0) {
            errorMessage = parsed.errors[0];
          }
        } catch (e) {
          // If parsing fails, use the original message
        }
      }
    }
    // Check for errors array
    else if (source.data.errors && Array.isArray(source.data.errors) && source.data.errors.length > 0) {
      errorMessage = source.data.errors[0];
    }
    // Check for error field
    else if (source.data.error) {
      errorMessage = source.data.error;
    }
    // Check for message field
    else if (source.data.message) {
      errorMessage = source.data.message;
    }
    // Check for response field
    else if (source.data.response) {
      errorMessage = source.data.response;
    }
  }

  // Handle error object from catch block (error.response.data)
  if (source?.response?.data) {
    const responseData = source.response.data;
    
    // Check for Failed field
    if (responseData.Failed) {
      errorMessage = responseData.Failed;
      // Try to parse if it's a JSON string
      if (typeof errorMessage === 'string' && errorMessage.includes('errors')) {
        try {
          const parsed = JSON.parse(errorMessage);
          if (parsed.errors && Array.isArray(parsed.errors) && parsed.errors.length > 0) {
            errorMessage = parsed.errors[0];
          }
        } catch (e) {
          // If parsing fails, use the original message
        }
      }
    }
    // Check for errors array
    else if (responseData.errors && Array.isArray(responseData.errors) && responseData.errors.length > 0) {
      errorMessage = responseData.errors[0];
    }
    // Check for error field
    else if (responseData.error) {
      errorMessage = responseData.error;
    }
    // Check for message field
    else if (responseData.message) {
      errorMessage = responseData.message;
    }
    // Check for response field
    else if (responseData.response) {
      errorMessage = responseData.response;
    }
  }

  // Also check source.message (set by useApi hook or error object) — but only
  // as a fallback: a generic transport message ("Bad Request") must not clobber
  // the specific error extracted from the response body above.
  if (source?.message && (!errorMessage || errorMessage === defaultMessage)) {
    errorMessage = source.message;
  }

  // Never surface a raw JSON blob (e.g. '{"errors": [...], "details": [...]}')
  // to the user; reduce it to its first human-readable error string.
  errorMessage = reduceJsonErrorBlob(errorMessage, defaultMessage);

  return errorMessage;
};

/**
 * If the message is a stringified validation payload, extract the first
 * error string from it instead of showing raw JSON to the user.
 */
const reduceJsonErrorBlob = (message: string, defaultMessage: string): string => {
  if (typeof message !== "string") return defaultMessage;
  const trimmed = message.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return message;
  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed) && parsed.length > 0) {
      const first = parsed[0];
      if (first?.message) return String(first.message);
      if (typeof first === "string" && first) return first;
      return defaultMessage;
    }
    if (Array.isArray(parsed?.errors) && parsed.errors.length > 0) {
      return String(parsed.errors[0]);
    }
    if (typeof parsed?.error === "string" && parsed.error) {
      return parsed.error;
    }
    if (typeof parsed?.message === "string" && parsed.message) {
      return parsed.message;
    }
    if (Array.isArray(parsed?.details) && parsed.details.length > 0) {
      const first = parsed.details[0];
      if (first?.message) return String(first.message);
    }
    return defaultMessage;
  } catch (e) {
    // Not JSON after all — show as-is.
    return message;
  }
};

export interface ValidationDetail {
  field: string;
  message: string;
}

// The dtrackit push stage rejects a submitted TC with 409 and this payload
// shape: {"Intermediate Ledger Validation Error": [{Reason, ErrorFiles:
// [{tcAssociatedProductNo, incomingTcNo, incomingLedgerProducts,
// incomingLedgerProductsWeight, availableWeight}]}]}. It previously surfaced
// as "Number already exists" / "Unable to Submit" (86ajcn0z8).
const LEDGER_VALIDATION_ERROR_KEY = "Intermediate Ledger Validation Error";

export const LEDGER_REJECTION_SUMMARY =
  "The TC submission was rejected by the intermediate ledger validation. Please review the messages below.";

const getLedgerValidationGroups = (data: any): any[] | undefined => {
  if (!data || typeof data !== "object") return undefined;
  const payload = parseFailedPayload(data.Failed) ?? data;
  const groups = payload?.[LEDGER_VALIDATION_ERROR_KEY];
  return Array.isArray(groups) && groups.length > 0 ? groups : undefined;
};

const ledgerGroupsToDetails = (groups: any[]): ValidationDetail[] =>
  groups.flatMap((group: any) => {
    const reason = String(group?.Reason ?? "Intermediate ledger validation failed").trim();
    const files = Array.isArray(group?.ErrorFiles) ? group.ErrorFiles : [];
    if (files.length === 0) {
      return [{ field: "", message: reason }];
    }
    return files.map((file: any) => {
      const where = [
        file?.incomingTcNo ? `Incoming TC ${file.incomingTcNo}` : "",
        file?.tcAssociatedProductNo ? `Product ${file.tcAssociatedProductNo}` : "",
      ]
        .filter(Boolean)
        .join(", ");
      const used = file?.incomingLedgerProductsWeight;
      const available = file?.availableWeight;
      const numbers =
        used !== undefined || available !== undefined
          ? ` (article ${file?.incomingLedgerProducts ?? "?"}: used ${used ?? "?"} kg, available ${available ?? 0} kg)`
          : "";
      return {
        field: "",
        message: `${where ? `${where}: ` : ""}${reason}${numbers}`,
      };
    });
  });

const parseFailedPayload = (failed: unknown): any => {
  if (typeof failed !== "string") return undefined;
  try {
    return JSON.parse(failed);
  } catch (e) {
    return undefined;
  }
};

/**
 * Extracts field-level validation details from API responses shaped like
 * {errors: [...], details: [{field, message}, ...]} — including responses where
 * that JSON arrives stringified under a `Failed` key (APIM/container-app wrapper).
 *
 * @param source - Either an API result object or an error object from catch block
 * @returns Array of {field, message} validation details (empty when none found)
 */
export const extractValidationDetails = (
  source: ApiResult | ApiErrorResponse | any
): ValidationDetail[] => {
  const candidates = [source?.data, source?.response?.data];
  for (const data of candidates) {
    if (!data) continue;
    const ledgerGroups = getLedgerValidationGroups(data);
    if (ledgerGroups) {
      return ledgerGroupsToDetails(ledgerGroups);
    }
    const payload = parseFailedPayload(data.Failed) ?? data;
    // Two shapes: {details: [{field, message}]} (final-JSON validator) and a
    // bare [{field, message}] array (product-save validator behind the
    // `Failed` wrapper, e.g. state/province format errors).
    const details = Array.isArray(payload?.details)
      ? payload.details
      : Array.isArray(payload)
      ? payload
      : undefined;
    if (details) {
      return details
        .filter((detail: any) => detail && (detail.field || detail.message))
        .map((detail: any) => ({
          field: String(detail.field ?? ""),
          message: String(detail.message ?? ""),
        }));
    }
  }
  return [];
};

/**
 * Error handler utility object
 */
export const ErrorHandler = {
  /**
   * Extracts error message from API response or error object
   * @param source - Either an API result object or an error object from catch block
   * @param defaultMessage - Default error message if no error can be extracted
   * @returns Extracted error message string
   */
  extractErrorMessage,
  /**
   * Extracts field-level validation details ({field, message}) from API responses
   * @param source - Either an API result object or an error object from catch block
   * @returns Array of validation details (empty when none found)
   */
  extractValidationDetails,
};

